import { Box, SimpleGrid, useMediaQuery } from '@chakra-ui/react';
import { differenceInSeconds } from 'date-fns';
import React, { useState, useEffect, useRef } from 'react';

import Testimonial from 'containers/Auth/Shared/Testimonial';
import { useRegister } from 'hooks/auth/useRegister';
import { useParamsQuery } from 'hooks/common/useParamsQuery';
import toaster from 'utils/toaster';

import OrganizationDetails from './OrganizationDetails';
import { initialOrganizationDetailsFormData } from './OrganizationDetails/schema';
import PersonalDetails from './PersonalDetails';
import { initialPersonalDetailsData } from './PersonalDetails/schema';

declare global {
  interface Window {
    gtag: any;
  }
}

function gtag_report_conversion() {
  window.gtag('event', 'conversion', { send_to: 'AW-10828039579/XbQuCOTwpZIDEJuLm6so' });
  return false;
}

const SignUp: React.FC<unknown> = () => {
  const [isLargerThan768] = useMediaQuery(['(min-width: 768px)'], { ssr: false });

  const { status, mutateAsync } = useRegister();

  const searchQuery = useParamsQuery();

  const [personalData, setPersonalData] = useState<typeof initialPersonalDetailsData>(initialPersonalDetailsData);
  const [organizationData, setOrganizationData] = useState<typeof initialOrganizationDetailsFormData>(
    initialOrganizationDetailsFormData
  );
  const [showOrganization, setShowOrganization] = useState<boolean>(false);
  const startTime = useRef(new Date());

  useEffect(() => {
    // google analytics
    window.gtag('config', import.meta.env.VITE_GOOGLE_CONVERSION_ID);

    // Add HubSpot Embed Code
    const hubSpotScript = document.createElement('script');
    hubSpotScript.id = 'hs-script-loader';
    hubSpotScript.src = '//js.hs-scripts.com/21677104.js';
    hubSpotScript.async = true;
    hubSpotScript.defer = true;
    document.body.appendChild(hubSpotScript);

    return () => {
      if (hubSpotScript && document.body.contains(hubSpotScript)) {
        document.body.removeChild(hubSpotScript);
      }
    };
  }, []);

  const handlePersonalDetailsFormSubmit = async (values: typeof initialPersonalDetailsData) => {
    setPersonalData(values);
    setShowOrganization(true);
  };

  const handleOrganizationDetailsFormSubmit = async (organizationData: typeof initialOrganizationDetailsFormData) => {
    setOrganizationData(organizationData);

    // Honeypot using elapsed time from loading page to form completion
    if (startTime.current) {
      const elapsedTimeInSeconds = differenceInSeconds(new Date(), startTime.current);
      if (elapsedTimeInSeconds < 10) {
        setShowOrganization(false);
        console.warn('Form submitted too quickly, likely a bot:', elapsedTimeInSeconds);
        toaster.error({ description: 'Unable to process your request at this time.' });
        return;
      }
    }

    const referralCode = searchQuery.get('referralCode');
    const referralCodeData = referralCode ? { referral_code: referralCode } : {};

    const hubstk = document.cookie.replace(/(?:(?:^|.*;\s*)hubspotutk\s*\=\s*([^;]*).*$)|^.*$/, '$1'); // get the hubstk from hubspot session
    /*
     * Honeypot for catching spam bot
     **/
    if (personalData?.to_message_A04 || organizationData?.to_notes_1A4) {
      setShowOrganization(false);
      console.warn('Spam detected:', personalData?.to_message_A04, organizationData?.to_notes_1A4);
      toaster.error({ description: 'Invalid Submission.' });
      return;
    }

    const { to_message_A04, showPassword, ...personalDataWithoutMessage } = personalData;
    const { to_notes_1A4, ...organizationDataWithoutNotes } = organizationData;

    const registrationData = {
      ...personalDataWithoutMessage,
      ...organizationDataWithoutNotes,
      job_title: personalData?.job_title.value,
      company_size: organizationData?.company_size.value,
      industry: organizationData?.industry.value,
      source: '',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      ...referralCodeData,
      hubstk,
    };
    gtag_report_conversion();
    mutateAsync(registrationData).catch(() => {
      setShowOrganization(false);
    });
  };

  return (
    <SimpleGrid minH={'100vh'} columns={{ base: 1, lg: 2 }} bg="white">
      {showOrganization ? (
        <OrganizationDetails
          status={status}
          handleOrganizationDetailsFormSubmit={handleOrganizationDetailsFormSubmit}
          formData={organizationData}
        />
      ) : (
        <PersonalDetails handlePersonalDetailsFormSubmit={handlePersonalDetailsFormSubmit} formData={personalData} />
      )}
      {isLargerThan768 && (
        <Box display={{ base: 'none', lg: 'block' }}>
          <Testimonial />
        </Box>
      )}
    </SimpleGrid>
  );
};

export default SignUp;
